import { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "./api";

const RECENT_KEY = "viq_google_sheets_recent_v247";
const normalizeSheetId = value => {
  const raw = String(value || "").trim();
  const m = raw.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  return (m?.[1] || raw).replace(/[^a-zA-Z0-9-_]/g, "");
};
const loadRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]").filter(x => x?.id).slice(0, 12); }
  catch (_) { return []; }
};
const saveRecent = rows => {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(rows.slice(0, 12))); }
  catch (_) {}
};

export default function GoogleSheetsModal({ onClose, onBack }) {
  const [diag, setDiag] = useState(null);
  const [recent, setRecent] = useState(loadRecent);
  const [sheetInput, setSheetInput] = useState("");
  const [createTitle, setCreateTitle] = useState("VehicleIQ - Export");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    apiGet("/api/integrations/google-sheets/diagnostic").then(d => { if (d) setDiag(d); }).catch(() => {});
  }, []);

  const remember = sheet => {
    const row = {
      id: sheet.id,
      title: sheet.title || "Google Sheet",
      url: sheet.url || `https://docs.google.com/spreadsheets/d/${sheet.id}/edit`,
      used_at: new Date().toISOString(),
    };
    const next = [row, ...recent.filter(x => x.id !== row.id)].slice(0, 12);
    setRecent(next); saveRecent(next);
    return row;
  };

  const openSheet = async () => {
    const id = normalizeSheetId(sheetInput);
    if (!id) { setError("Collez l’URL ou l’identifiant d’un Google Sheet."); return; }
    setBusy(true); setError("");
    try {
      const r = await apiGet(`/api/integrations/google-sheets/spreadsheet?spreadsheet_id=${encodeURIComponent(id)}&range=${encodeURIComponent("A1:A5")}`);
      if (!r?.success) throw new Error(r?.error || "Google Sheet inaccessible.");
      const row = remember({ id:r.spreadsheet?.id || id, title:r.spreadsheet?.title, url:`https://docs.google.com/spreadsheets/d/${r.spreadsheet?.id || id}/edit` });
      window.open(row.url, "_blank", "noopener,noreferrer");
      setSheetInput("");
    } catch (e) { setError(e?.message || "Google Sheet inaccessible."); }
    finally { setBusy(false); }
  };

  const createSheet = async () => {
    setBusy(true); setError("");
    try {
      const r = await apiPost("/api/integrations/google-sheets/create", { title:createTitle.trim() || "VehicleIQ - Export" });
      if (!r?.success) throw new Error(r?.error || "Création Google Sheets impossible.");
      const row = remember({ id:r.spreadsheet?.id, title:r.spreadsheet?.title, url:r.spreadsheet?.url });
      if (row?.url) window.open(row.url, "_blank", "noopener,noreferrer");
    } catch (e) { setError(e?.message || "Création Google Sheets impossible."); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? recent.filter(x => `${x.title} ${x.id}`.toLowerCase().includes(q)) : recent;
  }, [recent, query]);

  const formatDate = v => {
    if (!v) return "—";
    try { return new Date(v).toLocaleString("fr-FR", { dateStyle:"medium", timeStyle:"short" }); }
    catch (_) { return "—"; }
  };

  return <div className="ws-overlay" role="presentation" onMouseDown={e => e.target === e.currentTarget && onClose?.()}>
    <section className="ws-modal google-sheets-modal sheets-list-modal" role="dialog" aria-modal="true">
      <header className="ws-modal-head">
        <div>
          <div className="ws-eyebrow">Google Workspace · moindre privilège</div>
          <h2>Google Sheets</h2>
          <p>VehicleIQ n’explore pas votre Google Drive. Ouvrez explicitement un tableur par URL/ID ou créez-en un depuis VehicleIQ. Seul le scope Google Sheets nécessaire à la lecture/écriture est demandé.</p>
        </div>
        <div className="ws-modal-head-actions">
          {onBack && <button className="ws-btn secondary" onClick={onBack}>← Intégrations</button>}
          <button className="ws-icon-btn" onClick={onClose}>✕</button>
        </div>
      </header>

      <div className="sheets-list-toolbar" style={{gridTemplateColumns:"minmax(0,1fr) auto"}}>
        <input value={sheetInput} onChange={e => setSheetInput(e.target.value)} placeholder="URL ou identifiant Google Sheet" />
        <button className="ws-btn primary" onClick={openSheet} disabled={busy || !sheetInput.trim()}>{busy ? "Vérification…" : "Ouvrir"}</button>
      </div>

      <div className="sheets-list-toolbar" style={{gridTemplateColumns:"minmax(0,1fr) auto"}}>
        <input value={createTitle} onChange={e => setCreateTitle(e.target.value)} placeholder="Nom du nouveau tableur" />
        <button className="ws-btn secondary" onClick={createSheet} disabled={busy}>{busy ? "Création…" : "Créer un tableur"}</button>
      </div>

      <div className="sheets-meta">
        <span>{diag?.connected ? "Google Sheets connecté" : "Google Sheets à connecter"}</span>
        <span>{diag?.least_privilege ? "Aucun accès Google Drive · moindre privilège ✓" : "Vérification des permissions…"}</span>
      </div>

      {error && <div className="ws-note sheets-message">{error}</div>}
      <div className="ws-note sheets-message">Les tableurs ci-dessous sont uniquement ceux que vous avez ouverts ou créés depuis ce navigateur. VehicleIQ ne parcourt pas la liste de vos fichiers Drive.</div>

      <div className="sheets-list-toolbar" style={{gridTemplateColumns:"1fr"}}>
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Rechercher dans les tableurs récents…" />
      </div>

      <div className="sheets-list-wrap">
        {filtered.length === 0 ? (
          <div className="sheets-empty"><strong>Aucun tableur récent.</strong><span>Collez une URL Google Sheets ci-dessus ou créez un nouveau tableur VehicleIQ.</span></div>
        ) : filtered.map(sheet => (
          <article className="sheet-file-card" key={sheet.id}>
            <div className="sheet-file-icon">▦</div>
            <div className="sheet-file-main">
              <div className="sheet-file-title">{sheet.title}</div>
              <div className="sheet-file-meta">Utilisé {formatDate(sheet.used_at)}</div>
              <div className="sheet-file-description">ID : {sheet.id}</div>
            </div>
            <div className="sheet-file-actions">
              <a className="ws-btn secondary" href={sheet.url} target="_blank" rel="noopener noreferrer">Ouvrir dans Google Sheets ↗</a>
            </div>
          </article>
        ))}
      </div>
    </section>
  </div>;
}
